import { useState, useRef, useImperativeHandle, forwardRef, useEffect, useMemo, useCallback } from "react";
import { Table, Radio } from "antd";
import * as XLSX from "xlsx";
import ServiceExcelProduct from "./service-excel-product";
import "./service-excel.css";

const ServiceExcelProducts = forwardRef((props, ref) => {
  const { onProductsChange } = props || {};
  const [products, setProducts] = useState([]);
  const [validationErrors, setValidationErrors] = useState({}); // { "recordKey-fieldName": "error" | "warning" }
  const [editingRecord, setEditingRecord] = useState(null); // Запись, которая редактируется
  const [filterType, setFilterType] = useState("all"); // "all" | "errors" | "warnings"
  const productsRef = useRef(null);

  // Маппинг колонок Excel на поля таблиц
  const productsColumnMap = {
    "Внешний ID": "externalId",
    "Наименование": "name",
    "ед. изм.": "unit",
    "Цена продажи": "salePrice",
    "Вид": "vid",
    "Зарплата исп. %": "salaryExecutorPercent",
    "Зарплата прод. %": "salarySellerPercent",
    "Зарплата мeнэджеру %": "salaryManagerPercent",
    "Артикул": "article",
    "Цена прихода": "purchasePrice",
    "Длительность(мин.)": "duration",
    "Срок": "term",
    "Вид срока": "termVid",
    "Кол-во визитов": "visitCount",
    "Вид ограничения кол-ва визитов": "visitLimitVid"
  };

  // Валидация для таблицы Номенклатура
  const validateProductField = (fieldName, value, record, allProducts) => {
    const isEmpty = value === null || value === undefined || value === "";

    switch (fieldName) {
      case "externalId":
        if (isEmpty) return "error"; // Критическая ошибка
        // Проверка на дубли
        const duplicateExternalId = allProducts.filter(
          (n) => n.key !== record.key && n.externalId === value
        );
        if (duplicateExternalId.length > 0) return "error"; // Критическая ошибка
        return null;

      case "name":
        if (isEmpty) return "error"; // Критическая ошибка
        return null;

      case "unit":
        if (isEmpty) return "warning"; // Предупреждение
        return null;

      case "salePrice":
        if (isEmpty) return "warning"; // Предупреждение
        return null;

      case "vid":
        if (isEmpty) return "error"; // Критическая ошибка
        return null;

      case "salaryExecutorPercent":
        if (isEmpty) return "warning"; // Предупреждение
        if (value !== null && value !== undefined && (value < 0 || value > 100)) return "warning"; // Предупреждение
        return null;

      case "salarySellerPercent":
        if (isEmpty) return "warning"; // Предупреждение
        if (value !== null && value !== undefined && (value < 0 || value > 100)) return "warning"; // Предупреждение
        return null;

      case "salaryManagerPercent":
        if (isEmpty) return "warning"; // Предупреждение
        if (value !== null && value !== undefined && (value < 0 || value > 100)) return "warning"; // Предупреждение
        return null;

      case "article":
        if (isEmpty) return "warning"; // Предупреждение
        return null;

      case "purchasePrice":
        if (isEmpty) return "warning"; // Предупреждение
        if (value !== null && value !== undefined && value < 0) return "warning"; // Предупреждение
        return null;

      case "duration":
        if (isEmpty) return "warning"; // Предупреждение
        if (value !== null && value !== undefined && value < 0) return "warning"; // Предупреждение
        return null;

      case "term":
        if (isEmpty) return "warning"; // Предупреждение
        if (value !== null && value !== undefined && value < 0) return "warning"; // Предупреждение
        return null;

      case "termVid":
        if (isEmpty) return "warning"; // Предупреждение
        const validTermVidValues = [0, 1, 31, 365];
        if (value !== null && value !== undefined && !validTermVidValues.includes(value)) return "warning"; // Предупреждение
        return null;

      case "visitCount":
        if (isEmpty) return "warning"; // Предупреждение
        if (value !== null && value !== undefined && value < 0) return "warning"; // Предупреждение
        return null;

      case "visitLimitVid":
        if (isEmpty) return "warning"; // Предупреждение
        const validVisitLimitVidValues = [0, 1, 7];
        if (value !== null && value !== undefined && !validVisitLimitVidValues.includes(value)) return "warning"; // Предупреждение
        return null;

      default:
        return null;
    }
  };

  // Функция для валидации всех полей записи
  const validateRecord = (record, allProducts) => {
    const errors = {};
    const fieldsToValidate = ["externalId", "name", "unit", "salePrice", "vid", "salaryExecutorPercent", "salarySellerPercent", "salaryManagerPercent", "article", "purchasePrice", "duration", "term", "termVid", "visitCount", "visitLimitVid"];
    
    fieldsToValidate.forEach(fieldName => {
      const validation = validateProductField(fieldName, record[fieldName], record, allProducts);
      if (validation) {
        errors[`${record.key}-${fieldName}`] = validation;
      }
    });
    
    return errors;
  };

  const getCellClassName = useCallback((fieldName, record) => {
    const errorKey = `${record.key}-${fieldName}`;
    const validation = validationErrors[errorKey];
    
    let className = "service-excel-cell";
    if (validation === "error") {
      className += " service-excel-cell-error";
    } else if (validation === "warning") {
      className += " service-excel-cell-warning";
    }
    return className;
  }, [validationErrors]);

  const handleRowClick = useCallback((record) => {
    setEditingRecord({ ...record });
  }, []);

  const handleSave = (updatedRecord) => {
    setProducts(prev => {
      // Проверяем, существует ли запись в массиве
      const existingIndex = prev.findIndex(item => item.key === updatedRecord.key);
      let newProducts;
      
      if (existingIndex >= 0) {
        // Обновляем существующую запись
        newProducts = prev.map(item => 
          item.key === updatedRecord.key ? updatedRecord : item
        );
      } else {
        // Добавляем новую запись
        newProducts = [...prev, updatedRecord];
      }
      
      // Валидация после сохранения
      const recordErrors = validateRecord(updatedRecord, newProducts);
      
      setValidationErrors(validationErrorsPrev => {
        const filtered = Object.keys(validationErrorsPrev)
          .filter(key => !key.startsWith(`${updatedRecord.key}-`))
          .reduce((obj, key) => {
            obj[key] = validationErrorsPrev[key];
            return obj;
          }, {});
        return { ...filtered, ...recordErrors };
      });
      
      if (onProductsChange) onProductsChange(newProducts.length);
      return newProducts;
    });
    setEditingRecord(null);
  };

  const handleCancel = () => {
    setEditingRecord(null);
  };


  const addRow = () => {
    const newKey = Date.now();
    const newRecord = {
      key: newKey,
      externalId: "",
      name: "",
      unit: "",
      salePrice: null,
      vid: null,
      salaryExecutorPercent: null,
      salarySellerPercent: null,
      salaryManagerPercent: null,
      article: "",
      purchasePrice: null,
      duration: null,
      term: null,
      termVid: null,
      visitCount: null,
      visitLimitVid: null
    };
    setEditingRecord(newRecord);
  };

  const deleteRow = useCallback((record) => {
    setProducts(prev => {
      const newProducts = prev.filter(item => item.key !== record.key);
      if (onProductsChange) onProductsChange(newProducts.length);
      return newProducts;
    });
    // Удаляем все ключи валидации для удаленной записи
    setValidationErrors(prev => {
      const filtered = Object.keys(prev)
        .filter(key => !key.startsWith(`${record.key}-`))
        .reduce((obj, key) => {
          obj[key] = prev[key];
          return obj;
        }, {});
      return filtered;
    });
  }, [onProductsChange]);

  const clearProducts = () => {
    setProducts([]);
    setValidationErrors({});
    if (onProductsChange) onProductsChange(0);
  };

  const parseSheetData = (worksheet) => {
    if (!worksheet) return [];
    
    // Используем raw: false для правильной обработки дат
    const jsonData = XLSX.utils.sheet_to_json(worksheet, { 
      header: 1, 
      defval: "",
      raw: false,
      dateNF: "dd.mm.yyyy"
    });
    if (jsonData.length < 2) return []; // Нет заголовков или данных

    const headers = jsonData[0].map(h => String(h || "").trim());
    const rows = jsonData.slice(1);

    return rows.map((row, index) => {
      // Проверяем первую колонку - если пустая, пропускаем запись
      const firstColumnValue = row[0];
      if (firstColumnValue === "" || firstColumnValue === null || firstColumnValue === undefined) {
        return null; // Возвращаем null для фильтрации
      }
      
      const record = { key: `product-${Date.now()}-${index}-${Math.random()}` };
      headers.forEach((header, colIndex) => {
        // Ищем поле с учетом возможных различий в регистре и пробелах
        let fieldName = productsColumnMap[header];
        if (!fieldName) {
          const matchedKey = Object.keys(productsColumnMap).find(key => 
            key.toLowerCase().trim() === header.toLowerCase().trim()
          );
          if (matchedKey) {
            fieldName = productsColumnMap[matchedKey];
          }
        }
        
        if (fieldName) {
          let value = row[colIndex];
          
          // Обработка пустых значений
          if (value === "" || value === null || value === undefined) {
            value = null;
          } 
          // Обработка числовых полей
          else if (typeof value === "number") {
            value = value;
          } 
          // Обработка строковых значений
          else {
            value = String(value).trim();
            
            // Пытаемся преобразовать строку в число, если поле числовое
            const numericFields = [
              "vid", "salePrice", "salaryExecutorPercent", 
              "salarySellerPercent", "salaryManagerPercent", "purchasePrice",
              "duration", "term", "termVid", "visitCount", "visitLimitVid"
            ];
            if (numericFields.includes(fieldName) && value !== "") {
              const numValue = parseFloat(value.replace(",", "."));
              value = isNaN(numValue) ? null : numValue;
            }
          }
          
          record[fieldName] = value;
        }
      });
      return record;
    }).filter(record => {
      // Фильтруем null записи (где первая колонка была пустая)
      if (record === null) return false;
      // Фильтруем пустые строки - проверяем, есть ли хотя бы одно непустое значение
      const values = Object.values(record).filter(key => key !== "key");
      return values.some(value => value !== null && value !== "" && value !== undefined);
    });
  };

  const loadFromExcel = (worksheet) => {
    const productsData = parseSheetData(worksheet);
    // Создаем новый массив для гарантии обновления React
    setProducts([...productsData]);
    
    // Валидация после загрузки
    setTimeout(() => {
      const errors = {};
      productsData.forEach(record => {
        const recordErrors = validateRecord(record, productsData);
        Object.assign(errors, recordErrors);
      });
      setValidationErrors(errors);
      
      if (onProductsChange) onProductsChange(productsData.length);
    }, 0);
    
    return productsData.length;
  };

  // Синхронизация количества продуктов с родительским компонентом
  useEffect(() => {
    if (onProductsChange) {
      onProductsChange(products.length);
    }
  }, [products, onProductsChange]);

  // Проверка наличия критических ошибок
  const hasCriticalErrors = () => {
    for (const product of products) {
      if (validateProductField("externalId", product.externalId, product, products) === "error") return true;
      if (validateProductField("name", product.name, product, products) === "error") return true;
      if (validateProductField("vid", product.vid, product, products) === "error") return true;
    }
    return false;
  };

  // Получение данных для сохранения
  const getProductsData = () => {
    return products.map(product => ({
      Active: true,
      CompanyId: 0,
      Id: parseInt(product.externalId) || 0,
      Name: product.name || "",
      ExpUnit: product.unit || "",
      ExpPrice: product.salePrice !== null && product.salePrice !== undefined ? parseFloat(product.salePrice) : 0,
      Vid: product.vid !== null && product.vid !== undefined ? parseInt(product.vid) : null,
      Duration: product.duration !== null && product.duration !== undefined ? parseInt(product.duration) : 0,
      Srok: product.term !== null && product.term !== undefined ? parseInt(product.term) : 0,
      SrokVid: product.termVid !== null && product.termVid !== undefined ? parseInt(product.termVid) : 0,
      LimitVisitQty: product.visitCount !== null && product.visitCount !== undefined ? parseInt(product.visitCount) : 0,
      LimitVisitQtyVid: product.visitLimitVid !== null && product.visitLimitVid !== undefined ? parseInt(product.visitLimitVid) : 0
    }));
  };

  // Проверка наличия ошибок у записи
  const hasRecordError = useCallback((record) => {
    const fieldsToValidate = ["externalId", "name", "unit", "salePrice", "vid", "salaryExecutorPercent", "salarySellerPercent", "salaryManagerPercent", "article", "purchasePrice", "duration", "term", "termVid", "visitCount", "visitLimitVid"];
    const recordKey = String(record.key);
    
    for (const field of fieldsToValidate) {
      const errorKey = `${recordKey}-${field}`;
      if (validationErrors[errorKey] === "error") {
        return true;
      }
    }
    return false;
  }, [validationErrors]);

  // Проверка наличия предупреждений у записи
  const hasRecordWarning = useCallback((record) => {
    const fieldsToValidate = ["externalId", "name", "unit", "salePrice", "vid", "salaryExecutorPercent", "salarySellerPercent", "salaryManagerPercent", "article", "purchasePrice", "duration", "term", "termVid", "visitCount", "visitLimitVid"];
    const recordKey = String(record.key);
    
    for (const field of fieldsToValidate) {
      const errorKey = `${recordKey}-${field}`;
      if (validationErrors[errorKey] === "warning") {
        return true;
      }
    }
    return false;
  }, [validationErrors]);

  // Подсчет записей с ошибками и предупреждениями
  const getValidationCounts = () => {
    const errorKeys = new Set();
    const warningKeys = new Set();
    
    // Получаем множество существующих ключей записей (как строки для сравнения)
    const existingKeys = new Set(products.map(p => String(p.key)));
    
    // Получаем список всех полей для валидации
    const fieldsToValidate = ["externalId", "name", "unit", "salePrice", "vid", "salaryExecutorPercent", "salarySellerPercent", "salaryManagerPercent", "article", "purchasePrice", "duration", "term", "termVid", "visitCount", "visitLimitVid"];
    
    // Сначала собираем все ошибки и предупреждения по записям
    Object.entries(validationErrors).forEach(([key, type]) => {
      // Извлекаем ключ записи - находим последнее поле из списка полей в ключе
      let recordKey = null;
      for (const field of fieldsToValidate) {
        if (key.endsWith(`-${field}`)) {
          recordKey = key.slice(0, -(field.length + 1)); // Убираем "-fieldName" с конца
          break;
        }
      }
      
      // Если не нашли через поля, используем старый метод как fallback
      if (!recordKey) {
        const parts = key.split('-');
        if (parts.length > 1) {
          // Берем все части кроме последней (которая является fieldName)
          recordKey = parts.slice(0, -1).join('-');
        } else {
          recordKey = key;
        }
      }
      
      // Учитываем только ошибки для существующих записей
      if (recordKey && existingKeys.has(recordKey)) {
        if (type === "error") {
          errorKeys.add(recordKey);
        } else if (type === "warning") {
          warningKeys.add(recordKey);
        }
      }
    });
    
    return {
      errors: errorKeys.size,
      warnings: warningKeys.size
    };
  };

  // Предоставляем методы через ref
  useImperativeHandle(ref, () => ({
    clearProducts,
    loadFromExcel,
    hasCriticalErrors,
    getProductsData,
    getProducts: () => products,
    addRow,
    getValidationCounts
  }));

  const getProductsColumns = useMemo(() => [
    {
      title: "",
      width: 60,
      align: "center",
      render: (_, record) => (
        <img
          src="/images/trashcan.svg"
          alt="delete"
          onClick={(e) => {
            e.stopPropagation();
            deleteRow(record);
          }}
          className="img-edit-btn service-excel-action-icon"
        />
      ),
    },
    {
      title: "Внешний ID",
      dataIndex: "externalId",
      width: 150,
      render: (text, record) => {
        const className = getCellClassName("externalId", record);
        return <div className={className}>{text || ""}</div>;
      },
    },
    {
      title: "Наименование",
      dataIndex: "name",
      width: 200,
      render: (text, record) => {
        const className = getCellClassName("name", record);
        return <div className={className}>{text || ""}</div>;
      },
    },
    {
      title: "ед. изм.",
      dataIndex: "unit",
      width: 100,
      render: (text, record) => {
        const className = getCellClassName("unit", record);
        return <div className={className}>{text || ""}</div>;
      },
    },
    {
      title: "Цена продажи",
      dataIndex: "salePrice",
      width: 120,
      render: (text, record) => {
        const className = getCellClassName("salePrice", record) + " service-excel-cell-right";
        return <div className={className}>{text !== null && text !== undefined ? text : ""}</div>;
      },
    },
    {
      title: "Вид",
      dataIndex: "vid",
      width: 120,
      render: (text, record) => {
        const className = getCellClassName("vid", record) + " service-excel-cell-center";
        const vidText = text === 0 ? "Товар" : text === 1 ? "Услуга" : text === 2 ? "Абонемент" : text === 3 ? "Блок" : "";
        return <div className={className}>{vidText}</div>;
      },
    },
    {
      title: "Зарплата исп. %",
      dataIndex: "salaryExecutorPercent",
      width: 130,
      render: (text, record) => {
        const className = getCellClassName("salaryExecutorPercent", record) + " service-excel-cell-right";
        return <div className={className}>{text !== null && text !== undefined ? text : ""}</div>;
      },
    },
    {
      title: "Зарплата прод. %",
      dataIndex: "salarySellerPercent",
      width: 130,
      render: (text, record) => {
        const className = getCellClassName("salarySellerPercent", record) + " service-excel-cell-right";
        return <div className={className}>{text !== null && text !== undefined ? text : ""}</div>;
      },
    },
    {
      title: "Зарплата менеджеру %",
      dataIndex: "salaryManagerPercent",
      width: 150,
      render: (text, record) => {
        const className = getCellClassName("salaryManagerPercent", record) + " service-excel-cell-right";
        return <div className={className}>{text !== null && text !== undefined ? text : ""}</div>;
      },
    },
    {
      title: "Артикул",
      dataIndex: "article",
      width: 150,
      render: (text, record) => {
        const className = getCellClassName("article", record);
        return <div className={className}>{text || ""}</div>;
      },
    },
    {
      title: "Цена прихода",
      dataIndex: "purchasePrice",
      width: 120,
      render: (text, record) => {
        const className = getCellClassName("purchasePrice", record) + " service-excel-cell-right";
        return <div className={className}>{text !== null && text !== undefined ? text : ""}</div>;
      },
    },
    {
      title: "Длительность(мин.)",
      dataIndex: "duration",
      width: 140,
      render: (text, record) => {
        const className = getCellClassName("duration", record) + " service-excel-cell-right";
        return <div className={className}>{text !== null && text !== undefined ? text : ""}</div>;
      },
    },
    {
      title: "Срок",
      dataIndex: "term",
      width: 100,
      render: (text, record) => {
        const className = getCellClassName("term", record) + " service-excel-cell-right";
        return <div className={className}>{text !== null && text !== undefined ? text : ""}</div>;
      },
    },
    {
      title: "Вид срока",
      dataIndex: "termVid",
      width: 120,
      render: (text, record) => {
        const className = getCellClassName("termVid", record) + " service-excel-cell-center";
        const termVidText = text === 0 ? "<Нет>" : text === 1 ? "Дней" : text === 31 ? "Месяцев" : text === 365 ? "Лет" : "";
        return <div className={className}>{termVidText}</div>;
      },
    },
    {
      title: "Кол-во визитов",
      dataIndex: "visitCount",
      width: 120,
      render: (text, record) => {
        const className = getCellClassName("visitCount", record) + " service-excel-cell-right";
        return <div className={className}>{text !== null && text !== undefined ? text : ""}</div>;
      },
    },
    {
      title: "Вид ограничения кол-ва визитов",
      dataIndex: "visitLimitVid",
      width: 200,
      render: (text, record) => {
        const className = getCellClassName("visitLimitVid", record) + " service-excel-cell-right";
        return <div className={className}>{text !== null && text !== undefined ? text : ""}</div>;
      },
    },
  ], [deleteRow, getCellClassName]);

  const getTableHeight = (ref) => {
    if (!ref.current) return 400;
    const headerSize = ref.current.querySelector('.ant-table-thead')?.offsetHeight ?? 0;
    return ref.current ? (document.documentElement.offsetHeight - headerSize - ref.current.getBoundingClientRect().top - window.scrollY - 150) : 400;
  };

  // Компоненты для таблиц с убранным padding в body ячейках
  const tableComponents = {
    body: {
      cell: (props) => {
        const { children, ...restProps } = props;
        return (
          <td {...restProps} style={{ ...restProps.style, padding: 0 }}>
            {children}
          </td>
        );
      },
    },
  };

  // Фильтрация данных в зависимости от выбранного фильтра
  const filteredProducts = useMemo(() => {
    if (filterType === "all") {
      return products;
    } else if (filterType === "errors") {
      return products.filter(product => hasRecordError(product));
    } else if (filterType === "warnings") {
      return products.filter(product => hasRecordWarning(product));
    }
    return products;
  }, [products, filterType, hasRecordError, hasRecordWarning]);

  // Получаем счетчики для отображения
  const validationCounts = getValidationCounts();

  return (
    <>
      <div ref={productsRef}>
        <div style={{ marginBottom: 16, display: "flex", alignItems: "center", gap: 16 }}>
          <Radio.Group 
            value={filterType} 
            onChange={(e) => setFilterType(e.target.value)}
            buttonStyle="solid"
          >
            <Radio.Button value="all">
              Всего ({products.length})
            </Radio.Button>
            <Radio.Button value="errors">
              Ошибок ({validationCounts.errors})
            </Radio.Button>
            <Radio.Button value="warnings">
              Предупреждений ({validationCounts.warnings})
            </Radio.Button>
          </Radio.Group>
        </div>
        <Table
          dataSource={filteredProducts}
          columns={getProductsColumns}
          rowKey="key"
          size="small"
          pagination={false}
          scroll={{ x: "max-content", y: getTableHeight(productsRef) }}
          bordered
          components={tableComponents}
          onRow={(record) => ({
            onClick: () => handleRowClick(record),
            style: { cursor: "pointer" }
          })}
        />
      </div>
      {editingRecord && (
        <ServiceExcelProduct
          record={editingRecord}
          onSave={handleSave}
          onCancel={handleCancel}
        />
      )}
    </>
  );
});

ServiceExcelProducts.displayName = "ServiceExcelProducts";

export default ServiceExcelProducts;

